import {
  APIAccessLevelOption,
  APICurrentUser,
  APICurrentUserConfidentialFieldPermissions,
  APICurrentUserPermission,
  APIResourcePermissionAttributes,
  ArdoqId,
  OrgAccessLevel,
  PermissionAccessLevel,
  PrivilegeLabel,
} from '@ardoq/api-types';
import {
  AddApiResourcePermissionAttributePayload,
  UnauthenticatedPermissionContext,
  PermissionContextWithConfidentialFields,
  LoadedPermissionContext,
  PermissionContext,
  PermissionContextUser,
  UserResourcePermissions,
  UserResourcePermissionsRecord,
} from './types';
import { getPermissionLabel } from './permissionHelpers';
import { userOperations } from '@ardoq/core';
import { confidentialFieldPermissionsOperations } from './confidentialFieldPermissionsOperations';

const toPermissionContext = (
  user: PermissionContextUser,
  permissions: UserResourcePermissionsRecord,
  confidentialFieldPermissions: APICurrentUserConfidentialFieldPermissions | null
): LoadedPermissionContext => {
  return {
    user,
    permissions,
    confidentialFieldPermissions,
  };
};

/**
 * Represents public users and the default state before the user is
 * authenticated.
 */
const createUnauthenticatedPermissionContext = (
  confidentialFieldPermissions?: APICurrentUserConfidentialFieldPermissions | null
): UnauthenticatedPermissionContext => ({
  user: null,
  permissions: {},
  confidentialFieldPermissions: confidentialFieldPermissions || null,
});

/**
 * @deprecated Use `createUnauthenticatedPermissionContext` instead
 */
const createEmptyPermissionContext = createUnauthenticatedPermissionContext;

/**
 * Transform APICurrentUserPermission to the package's internal permission
 * structure.
 */
const fromApiUserResourcePermission = ({
  permissions,
  resourceId,
  resourceType,
}: APICurrentUserPermission): UserResourcePermissions => {
  const accessLevel = toHighestNumericAccessLevel(permissions);

  return {
    resourceId,
    resourceType,
    hasAdminAccess: accessLevel > 2,
    hasEditAccess: accessLevel > 1,
    hasReadAccess: accessLevel > 0,
  };
};

/**
 * Transforms the user resource permissions object from the API into the format
 * we use in this module.
 * Note that it expects all permissions to apply to the same user, which is
 * unlike addApiResourcePermissionAttribute
 */
const fromApiUserResourcePermissions = (
  permissions: APICurrentUserPermission[]
): UserResourcePermissionsRecord => {
  return Object.fromEntries(
    permissions.map(permission => [
      permission.resourceId,
      permissionsOperations.fromApiUserResourcePermission(permission),
    ])
  );
};

/**
 * To easily compare organisation access levels it is useful to convert them to numbers.
 * This should never be exposed outside this module.
 */
const _organisationAccessLevelToNumber: Record<OrgAccessLevel, number> = {
  [OrgAccessLevel.CONTRIBUTOR]: 1,
  [OrgAccessLevel.READER]: 2,
  [OrgAccessLevel.WRITER]: 3,
  [OrgAccessLevel.ADMIN]: 4,
};
const parseOrganisationAccessLevelAsNumber = (level: OrgAccessLevel) => {
  return _organisationAccessLevelToNumber[level] ?? 0;
};

/**
 * To easily compare access levels it is useful to convert them to numbers.
 * This should never be exposed outside this module.
 */
const _permissionAccessLevelToNumber: Record<APIAccessLevelOption, number> = {
  none: 0,
  [PermissionAccessLevel.READ]: 1,
  [PermissionAccessLevel.EDIT]: 2,
  [PermissionAccessLevel.ADMIN]: 3,
};
const parsePermissionAccessLevelAsNumber = (
  level: APIAccessLevelOption | null
) => {
  if (!level) {
    return 0;
  }
  return _permissionAccessLevelToNumber[level] ?? 0;
};

/**
 * Converts access level to a number that can be compared
 */
const toHighestNumericAccessLevel = (
  permissionLevels: PermissionAccessLevel[]
) => {
  const accessLevels = permissionLevels.map(parsePermissionAccessLevelAsNumber);
  return Math.max(0, ...accessLevels);
};

/**
 * Converts a user/group scoped permission into a numeric access level that can
 * be compared
 */
const toHighestNumericResourceAccessLevel = (
  permissions: Array<{ permissions: PermissionAccessLevel[] }>
) => {
  const accessLevels = permissions.map(resourcePermission => {
    return toHighestNumericAccessLevel(resourcePermission.permissions);
  });

  return Math.max(0, ...accessLevels);
};

/**
 * Extract the resource permissions that apply directly to a user
 */
const getUserScopedResourcePermissions = (args: {
  user: { _id: APICurrentUser['_id'] };
  attributes: {
    permissions: APIResourcePermissionAttributes['permissions'];
    groups: APIResourcePermissionAttributes['groups'];
  };
}) => {
  const { user, attributes } = args;
  return attributes.permissions.filter(
    permission => permission._id === user._id
  );
};

/**
 * Extract the resource permissions that apply to a user based on their group/role
 */
const getGroupScopedResourcePermissions = (args: {
  user: {
    roles: APICurrentUser['roles'];
    _id: APICurrentUser['_id'];
    groups?: APICurrentUser['groups'];
  };
  attributes: {
    permissions: APIResourcePermissionAttributes['permissions'];
    groups: APIResourcePermissionAttributes['groups'];
  };
}) => {
  const { user, attributes } = args;
  return attributes.groups.filter(
    group =>
      user.roles.includes(group.name) || user.groups?.includes(group.name)
  );
};

/**
 * Transform APIResourcePermissionAttributes to the package's internal
 * permission structure.
 */
const fromApiResourcePermissionAttributes = (
  user: {
    _id: APICurrentUser['_id'];
    roles: APICurrentUser['roles'];
    groups?: APICurrentUser['groups'];
  },
  attributes: {
    permissions: APIResourcePermissionAttributes['permissions'];
    groups: APIResourcePermissionAttributes['groups'];
    resource: APIResourcePermissionAttributes['resource'];
    'resource-type': APIResourcePermissionAttributes['resource-type'];
  }
): UserResourcePermissions => {
  const userPermissions = getUserScopedResourcePermissions({
    user,
    attributes,
  });
  const groupPermissions = getGroupScopedResourcePermissions({
    user,
    attributes,
  });

  const accessLevel = Math.max(
    0,
    toHighestNumericResourceAccessLevel(userPermissions),
    toHighestNumericResourceAccessLevel(groupPermissions)
  );

  return {
    resourceId: attributes.resource,
    resourceType: attributes['resource-type'],
    hasAdminAccess: accessLevel > 2,
    hasEditAccess: accessLevel > 1,
    hasReadAccess: accessLevel > 0,
  };
};

/**
 * Reducer style function to add a resource permission to the permissions record
 */
const addApiResourcePermissionAttribute = (
  state: UserResourcePermissionsRecord,
  payload: AddApiResourcePermissionAttributePayload
) => {
  const { permission, user } = payload;
  const { resource: resourceId } = permission;

  return {
    ...state,
    [resourceId]: permissionsOperations.fromApiResourcePermissionAttributes(
      user,
      permission
    ),
  };
};

const isUserFullyLoaded = (
  user: PermissionContextUser | null
): user is PermissionContextUser => {
  return Boolean(user?._id && user.roles && user.organization?.label);
};

/**
 * Check if the user has admin role in the organization.
 */
const hasAdminRole = (permissionContext: PermissionContext) => {
  if (!isLoadedPermissionContext(permissionContext)) {
    return false;
  }
  return userOperations.hasAdminRole(permissionContext.user);
};

/**
 * Check if the user has writer role in the organization.
 * If you need to check "can the user write data" use `hasWriterRolePlus`
 */
const hasWriterRole = (permissionContext: PermissionContext) => {
  if (!isLoadedPermissionContext(permissionContext)) {
    return false;
  }
  return userOperations.hasWriterRole(permissionContext.user);
};

/**
 * Check if the user has role that can write.
 */
const hasWriterRolePlus = (permissionContext: PermissionContext) => {
  if (!isLoadedPermissionContext(permissionContext)) {
    return false;
  }
  return userOperations.hasWriterRolePlus(permissionContext.user);
};

/**
 * Check if the user has reader role in the organization.
 * If you need to check "can the user read data" use `hasReaderRolePlus`
 */
const hasReaderRole = (permissionContext: PermissionContext) => {
  if (!isLoadedPermissionContext(permissionContext)) {
    return false;
  }
  return userOperations.hasReaderRole(permissionContext.user);
};

/**
 * Check if the user has role that can read.
 */
const hasReaderRolePlus = (permissionContext: PermissionContext) => {
  if (!isLoadedPermissionContext(permissionContext)) {
    return false;
  }
  return userOperations.hasReaderRolePlus(permissionContext.user);
};

/**
 * @deprecated Use `userOperations.hasAdminRole` or `permissionsOperations.hasAdminRole` instead
 */
const isOrgAdmin = (user: PermissionContextUser | null) => {
  if (!isUserFullyLoaded(user)) {
    return false;
  }
  const orgLabel = user.organization.label;

  return (
    getUserRole(user) === OrgAccessLevel.ADMIN ||
    user.roles.includes(`${orgLabel}/${OrgAccessLevel.ADMIN}`)
  );
};

/**
 * @deprecated Use `userOperations.hasWriterRolePlus` or `permissionsOperations.hasWriterRolePlus` instead
 */
const isOrgWriter = (user: PermissionContextUser) => {
  return isOrgAdmin(user) || hasWriteAccess(user);
};

/**
 * Check if the user in the PermissionContext has contributor role in the organization.
 */
const hasOrgContributorRole = (permissionContext: PermissionContext) => {
  if (!isLoadedPermissionContext(permissionContext)) {
    return false;
  }
  return userOperations.hasOrgContributorRole(permissionContext.user);
};

/**
 * Check if the user is at least a reader in the organization.
 * Otherwise, the user is a Contributor.
 * And is not allowed to see any resources in the
 * organization (except in Discover or Surveys)
 *
 * NOTE: This comes with the assumption that the user has access to the organization (i.e. is an org user)
 * .
 * @deprecated Use `userOperations.hasReaderRolePlus` or `permissionsOperations.hasReaderRolePlus` instead
 */
const isOrgReader = (user: PermissionContextUser) => {
  return (
    isOrgAdmin(user) || isOrgWriter(user) || userOperations.hasReaderRole(user)
  );
};

/**
 * @deprecated Use `userOperations.isArdoqEmployee`
 */
const isArdoqEmployee = (
  context: Pick<LoadedPermissionContext, 'user'>
): boolean => context.user.email?.endsWith('@ardoq.com') ?? false;

const getOrgIdFromContext = (
  context: Pick<LoadedPermissionContext, 'user'>
) => {
  return context.user.organization._id;
};

const getUserRole = (user: LoadedPermissionContext['user']) => {
  return user.organization.role;
};

/**
 * Equivalent to hasWriterRolePlus
 * @deprecated Use `userOperations.hasWriterRolePlus` or `permissionsOperations.hasWriterRolePlus` instead
 */
const hasWriteAccess = (user: PermissionContextUser) => {
  return user.writeAccess;
};

const hasReadAccess = (
  _user: PermissionContextUser,
  currentUserPermissions: UserResourcePermissionsRecord,
  resourceId: ArdoqId
) => {
  const hasReadAccess = hasResourceReadAccess(
    currentUserPermissions,
    resourceId
  );
  return hasReadAccess;
};

const hasEditAccess = (
  user: PermissionContextUser,
  userPermissions: UserResourcePermissionsRecord,
  resourceId: string
) => {
  const userHasWriteAccess = hasWriteAccess(user);

  return (
    userHasWriteAccess && hasResourceEditAccess(userPermissions, resourceId)
  );
};

const hasAdminAccess = (
  user: PermissionContextUser,
  permissions: UserResourcePermissionsRecord,
  resourceId: ArdoqId
) => {
  const userHasWriteAccess = hasWriteAccess(user);

  return userHasWriteAccess && hasResourceAdminAccess(permissions, resourceId);
};

/**
 * Read resource permissions
 */
const getPermissionsByResourceId = (
  permissions: UserResourcePermissionsRecord,
  resourceId: string
): UserResourcePermissions | undefined => {
  const resourcePermissions = permissions[resourceId];
  return resourcePermissions;
};

/**
 * Convenience function around getPermissionsByResourceId
 */
const hasResourceAdminAccess = (
  permissions: UserResourcePermissionsRecord,
  resourceId: string
) => {
  const resourcePermissions = getPermissionsByResourceId(
    permissions,
    resourceId
  );
  return Boolean(resourcePermissions?.hasAdminAccess);
};

/**
 * Convenience function around getPermissionsByResourceId
 */
const hasResourceEditAccess = (
  permissions: UserResourcePermissionsRecord,
  resourceId: string
) => {
  const resourcePermissions = getPermissionsByResourceId(
    permissions,
    resourceId
  );
  return Boolean(resourcePermissions?.hasEditAccess);
};

/**
 * Convenience function around getPermissionsByResourceId
 */
const hasResourceReadAccess = (
  permissions: UserResourcePermissionsRecord,
  resourceId: string
) => {
  const resourcePermissions = getPermissionsByResourceId(
    permissions,
    resourceId
  );
  return Boolean(resourcePermissions?.hasReadAccess);
};

/**
 * Convenience function around getPermissionsByResourceId
 */
const hasResourcePermission = (
  permissions: UserResourcePermissionsRecord,
  resourceId: string
) => {
  const resourcePermissions = getPermissionsByResourceId(
    permissions,
    resourceId
  );
  return Boolean(resourcePermissions);
};

/**
 * PermissionContext-based wrapper around hasResourceAdminAccess. Prefer this at
 * call sites that should treat PermissionContext as opaque.
 */
const hasAdminAccessToResource = (
  permissionContext: PermissionContext,
  resourceId: string
): boolean => hasResourceAdminAccess(permissionContext.permissions, resourceId);

/**
 * PermissionContext-based wrapper around hasResourceEditAccess. Prefer this at
 * call sites that should treat PermissionContext as opaque.
 */
const hasEditAccessToResource = (
  permissionContext: PermissionContext,
  resourceId: string
): boolean => hasResourceEditAccess(permissionContext.permissions, resourceId);

/**
 * PermissionContext-based wrapper around hasResourceReadAccess. Prefer this at
 * call sites that should treat PermissionContext as opaque.
 */
const hasReadAccessToResource = (
  permissionContext: PermissionContext,
  resourceId: string
): boolean => hasResourceReadAccess(permissionContext.permissions, resourceId);
/**
 * Convenience function to check if the user has been loaded
 */
const isLoadedPermissionContext = (
  permissionContext: PermissionContext
): permissionContext is LoadedPermissionContext => {
  return Boolean(
    permissionContext.user?._id &&
    permissionContext.user.organization?._id &&
    permissionContext.permissions
  );
};

/**
 * Get user OrgAccessLevel
 */
const getUserOrgAccessLevel = (user: PermissionContextUser) => {
  if (isOrgAdmin(user)) {
    return OrgAccessLevel.ADMIN;
  }
  if (isOrgWriter(user)) {
    return OrgAccessLevel.WRITER;
  }
  if (isOrgReader(user)) {
    return OrgAccessLevel.READER;
  }
  return OrgAccessLevel.CONTRIBUTOR;
};

/**
 * Check if the user has minimum access
 */
const hasRequiredOrgAccessLevel = (
  requiredAccessLevel: OrgAccessLevel,
  currentUser: PermissionContextUser
) => {
  const requiredAccessLevelAsNumber =
    parseOrganisationAccessLevelAsNumber(requiredAccessLevel);
  const userAccessLevelAsNumber = parseOrganisationAccessLevelAsNumber(
    getUserOrgAccessLevel(currentUser)
  );
  return userAccessLevelAsNumber >= requiredAccessLevelAsNumber;
};

const getAllowedPermissionsLevels = (): (PermissionAccessLevel | null)[] => [
  PermissionAccessLevel.ADMIN,
  PermissionAccessLevel.EDIT,
  PermissionAccessLevel.READ,
  null,
];

/**
 * This does not check if permissionContext is loaded because in public
 * presentations we will not have a user, but still need to respond to
 * confidentiality queries.
 */
const isConfidentialFieldsEnabled = (
  permissionContext: PermissionContext
): permissionContext is PermissionContextWithConfidentialFields => {
  return (
    'confidentialFieldPermissions' in permissionContext &&
    Boolean(permissionContext.confidentialFieldPermissions)
  );
};

const canSeeConfidentialFieldByName = (
  permissionContext: PermissionContext,
  fieldName: string,
  workspaceId: string
): boolean => {
  if (!isConfidentialFieldsEnabled(permissionContext)) {
    return true;
  }

  return confidentialFieldPermissionsOperations.canSeeConfidentialFieldByName(
    permissionContext.confidentialFieldPermissions,
    fieldName,
    workspaceId
  );
};

/**
 * Answers "which fields in the given workspace are redacted for the current user?"
 */
const redactedFieldsInWorkspace = (
  permissionContext: PermissionContext,
  workspaceId: string
): string[] => {
  if (!isConfidentialFieldsEnabled(permissionContext)) {
    return [];
  }

  return confidentialFieldPermissionsOperations.fieldsInWorkspaceUserCannotSee(
    permissionContext.confidentialFieldPermissions,
    workspaceId
  );
};

/**
 * Checks if the current user has a given privilege
 */
const hasPrivilege = (
  permissionContext: PermissionContext,
  privilegeLabel: PrivilegeLabel
) => {
  if (!isLoadedPermissionContext(permissionContext)) {
    return false;
  }
  const organization = permissionContext.user.organization;
  if (!organization) return false;
  const privileges = permissionContext.user.privileges[organization._id];
  if (!privileges) return false;
  return privileges.some(({ label }) => label === privilegeLabel);
};

const hasDiscoverInCoreAccess = (permissionContext: PermissionContext) =>
  hasAdminRole(permissionContext) ||
  hasPrivilege(permissionContext, PrivilegeLabel.ACCESS_DISCOVER_IN_CORE);

const hasAnyDiscoverAccess = (permissionContext: PermissionContext) =>
  hasPrivilege(permissionContext, PrivilegeLabel.ACCESS_DISCOVER) ||
  hasDiscoverInCoreAccess(permissionContext);

const getConfidentialFields = (
  permissionContext: PermissionContext
): string[] => {
  if (!isConfidentialFieldsEnabled(permissionContext)) {
    return [];
  }
  return [
    ...permissionContext.confidentialFieldPermissions.allConfidentialFieldNames,
  ];
};

/**
 * Checks whether a field is marked as confidential based on the permission context.
 *
 * Note: For date range fields, the name must be the normalized start-date name
 * (as returned by `getStartDateName`). Prefer using `fieldIsConfidential` from
 * `confidentialFieldUtil.ts`, which handles this normalization automatically.
 */
const fieldIsConfidential = (
  permissionContext: PermissionContext,
  fieldName: string
): boolean => {
  if (!isConfidentialFieldsEnabled(permissionContext)) {
    return false;
  }
  return permissionContext.confidentialFieldPermissions.allConfidentialFieldNames.includes(
    fieldName
  );
};

export const permissionsOperations = {
  addApiResourcePermissionAttribute,
  canSeeConfidentialFieldByName,
  redactedFieldsInWorkspace,
  getOrgIdFromContext,
  hasAdminAccess,
  hasEditAccess,
  hasReadAccess,
  hasResourceAdminAccess,
  hasResourceEditAccess,
  hasResourceReadAccess,
  hasResourcePermission,
  hasAdminAccessToResource,
  hasEditAccessToResource,
  hasReadAccessToResource,
  isOrgAdmin,
  isOrgWriter,
  isOrgReader,
  isConfidentialFieldsEnabled,
  hasAdminRole,
  hasWriterRole,
  hasWriterRolePlus,
  hasReaderRole,
  hasReaderRolePlus,
  isArdoqEmployee,
  toPermissionContext,
  /**
   * @deprecated Use `createUnauthenticatedPermissionContext` instead
   */
  createEmptyPermissionContext,
  createUnauthenticatedPermissionContext,
  fromApiUserResourcePermission,
  fromApiUserResourcePermissions,
  fromApiResourcePermissionAttributes,
  getPermissionLabel,
  isLoadedPermissionContext,
  getUserOrgAccessLevel,
  hasRequiredOrgAccessLevel,
  toHighestNumericAccessLevel,
  parsePermissionAccessLevelAsNumber,
  getAllowedPermissionsLevels,
  hasOrgContributorRole,
  isUserFullyLoaded,
  hasPrivilege,
  hasDiscoverInCoreAccess,
  hasAnyDiscoverAccess,
  getConfidentialFields,
  fieldIsConfidential,
};
